/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IScope, TTypeDesc, TYPE_DESC_TYPES } from "@adapptio/lib-blueprint";
import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import React, { FunctionComponent, useCallback, useMemo } from "react";
import { PanelNote } from "../Panel";
import { ITreeViewProps, TreeView } from "../Tree/TreeView";
import { ITreeNode, TREE_VIEW_STYLE } from "../Tree/TreeViewTypes";
import { useTreeSelections } from "../Tree/useTreeSelections";
import { useTreeState } from "../Tree/useTreeState";
import { useContextMenu } from "../../hooks/useContextMenu";
import { useReferenceSelectorContext } from "../../contexts/ReferenceSelectorContext";
import { isString } from "@adapptio/lib-shared";
import { serializeDataKey } from "../../../utils/serializeDataKey";

const ARRAY_BUCKET_SIZE = 30;

function addKeyToPath(path: string, key: number | string): string {
	const serializedKey = serializeDataKey(key);

	if (!path) {
		return serializedKey;
	}

	if (serializedKey.startsWith("[")) {
		return path + serializedKey;
	}

	return `${path}.${serializedKey}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTreeNode(
	t: TTranslateHookFn,
	{
		id,
		key,
		data,
		type,
		path = ""
	}: { id: string; key: string; data: any; type: TTypeDesc; path?: string },
	resolveChildren = 0
): ITreeNode {
	// @todo remove type union
	const node: ITreeNode = {
		id: id,
		label: key,
		value: undefined,
		description: undefined,
		tooltip: type?.label ? t("editor", type.label) : undefined,
		flags: [],
		isSelected: false,
		isLoadable: false,
		children: null,
		metaData: {
			refData: data,
			refType: type,
			key: key,
			path: path
		}
	};

	if (Array.isArray(data)) {
		if (data.length > 0) {
			node.isLoadable = true;
		} else {
			node.value = "[]";
		}

		if (resolveChildren > 0) {
			const itemTypes: TTypeDesc[] = type?.type === TYPE_DESC_TYPES.ARRAY ? type.items : [];

			const buckets: ITreeNode[] = [];
			let lastIndex = 0;

			data.forEach((item, i) => {
				const child = getTreeNode(
					t,
					{
						id: `${id}[${i}]`,
						key: String(i),
						data: item,
						type: itemTypes[i],
						path: addKeyToPath(path, i)
					},
					resolveChildren - 1
				);

				if (child) {
					const bucketIndex = Math.floor(lastIndex / ARRAY_BUCKET_SIZE);

					if (!buckets[bucketIndex]) {
						const _startIndex = bucketIndex * ARRAY_BUCKET_SIZE;
						const _endIndex = Math.min(data.length, (bucketIndex + 1) * ARRAY_BUCKET_SIZE) - 1;

						buckets[bucketIndex] = {
							id: `${id}#${bucketIndex}`,
							label: `[${_startIndex}...${_endIndex}]`,
							value: undefined,
							isSelected: false,
							children: []
						};
					}

					buckets[bucketIndex].children.push(child);
					lastIndex++;
				}
			});

			if (buckets.length === 1) {
				node.children = buckets[0].children;
			} else {
				node.children = buckets;
			}
		}
	} else if (data instanceof Date) {
		return {
			id: id,
			label: key,
			value: data.toISOString(),
			description: type?.label ? t("editor", type.label) : undefined,
			isSelected: false,
			children: []
		};
	} else if (typeof data === "function") {
		if (type?.type === TYPE_DESC_TYPES.METHOD) {
			const args = type.argRequiredCount > 0 ? type.argSchemas.map((argSchema) => argSchema?.name) : [];
			const argsString = args.join(", ");

			node.flags.push({
				icon: "mdi/function"
			});

			node.value = `(${argsString})`;
		} else if (type?.type === TYPE_DESC_TYPES.FUNCTION) {
			const args = type.argRequiredCount > 0 ? type.argSchemas.map((argSchema) => argSchema?.name) : [];
			const argsString = args.join(", ");

			node.flags.push({
				icon: "mdi/function-variant"
			});

			node.value = `(${argsString})`;
		} else {
			node.flags.push({
				icon: "mdi/function-variant"
			});

			node.value = `()`;
		}
	} else if (data instanceof Object) {
		if (Object.keys(data).length > 0) {
			node.isLoadable = true;
		} else {
			node.value = "{}";
		}

		if (resolveChildren > 0 && Object.keys(data).length > 0) {
			let propsType: { [K: string]: TTypeDesc };

			node.children = [];

			switch (type?.type) {
				case TYPE_DESC_TYPES.OBJECT: {
					propsType = type.props;
					break;
				}
				case TYPE_DESC_TYPES.MAP: {
					propsType = type.items;
					break;
				}
				default: {
					propsType = {};
				}
			}

			for (const k in data) {
				if (k === "globals" || k.startsWith("__")) {
					continue;
				}

				const child = getTreeNode(
					t,
					{
						id: `${id}.${k}`,
						key: k,
						data: data[k],
						type: propsType[k],
						path: addKeyToPath(path, k)
					},
					resolveChildren - 1
				);

				if (child) {
					node.children.push(child);
				}
			}
		}
	} else {
		node.value = typeof data === "string" ? `"${data}"` : String(data);
	}

	return node;
}

/**
 * Component props
 */
export interface IScopeViewerProps {
	scope?: IScope;
	noScopeNote?: string;
	defaultLevelsExpanded?: number;
	sort?: boolean;
}

/**
 * Center content component
 */
export const ScopeViewer: FunctionComponent<IScopeViewerProps> = ({
	scope,
	noScopeNote,
	defaultLevelsExpanded,
	sort
}) => {
	const baseClass = BEM("edt-scope-viewer");
	const t = useTranslate();
	const [ selections, selectNode ] = useTreeSelections();
	const [ treeState, handleTreeStateUpdate ] = useTreeState();
	const { showMenu } = useContextMenu("scopeNode");
	const referenceSelectorCtx = useReferenceSelectorContext();

	const treeItems = useMemo(() => {
		if (!scope) {
			return [];
		}

		const root = getTreeNode(
			t,
			{ id: "$", key: "$", data: scope.globalData, type: scope.globalType },
			30
		);
		return root ? root.children : [];
	}, [ scope ]);

	const handleContextMenuClick = useCallback<ITreeViewProps["onNodeContextMenu"]>(
		(treeNode, event) => {
			showMenu(event, { treeNode });
		},
		[ showMenu ]
	);

	const handleNodeClick = useCallback<ITreeViewProps["onNodeClick"]>((node, event) => {
		if (event.metaKey) {
			const metaData = node.metaData;
			const refData = metaData?.refData;

			const value = isString(refData) ? refData : JSON.stringify(refData);
			const path = metaData?.path as string;

			referenceSelectorCtx?.selectReference({
				value: value,
				path: path
			});
		}
	}, []);

	if (scope) {
		return (
			<TreeView
				className={baseClass.toClassName()}
				items={treeItems}
				state={treeState}
				selections={selections}
				onNodeClick={handleNodeClick}
				onNodeSelect={selectNode}
				onStateUpdate={handleTreeStateUpdate}
				style={TREE_VIEW_STYLE.MONOSPACE}
				defaultLevelsExpanded={defaultLevelsExpanded}
				onNodeContextMenu={handleContextMenuClick}
				compact
				sort={sort}
			/>
		);
	} else {
		return <PanelNote>{noScopeNote}</PanelNote>;
	}
};
